<template>
    <div class="mt-8 py-2">
        <div @click="$emit('closed')" class="btn btn-square btn-sm btn-ghost absolute top-0 right-0 m-1 close-z-100">
            <span class="material-symbols-outlined notranslate fill-current">
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
                    <path
                        d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                </svg>
            </span>
        </div>
        <div class="mb-1 mx-4 flex justify-between">
            <div class="text-lg font-extrabold mb-1 pb-2 mx-2">{{ trans.transit }}</div>
            <div class="btn btn-sm rounded-full normal-case" @click="clearFilter">{{ trans.search_box.clear }}</div>
        </div>
        <div class="overflow-y-auto h-screen locale-content-height">
            <div class="mx-4">
                <ul class="menu w-full">
                    <li v-for="item in gtfs" :key="item.id" v-show="item.routes.length">
                        <details open>
                            <summary class="font-extrabold">{{ item.title }}</summary>
                            <ul class="menu overflow-visible">
                                <div v-for="route in item.routes" :key="route.id">
                                    <label class="wrap-text w-fit label cursor-pointer"
                                        @click="chooseRoute(item, route)" :for="route.route_id + item.id">
                                        <input type="checkbox" :id="route.route_id + item.id" class="checkbox mr-2"
                                            :value="route.route_id" v-model="filterRoutes">
                                        <span class="label-text flex items-center">
                                            <span class="mr-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px"
                                                    viewBox="0 -960 960 960" width="24px"
                                                    :fill="'#' + route.route_color">
                                                    <path
                                                        d="M480-280q-73 0-127.5-45.5T284-440H80v-80h204q14-69 68.5-114.5T480-680q73 0 127.5 45.5T676-520h204v80H676q-14 69-68.5 114.5T480-280Zm0-80q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Z" />
                                                </svg>
                                            </span>
                                            <div>{{ route.route_id }}</div>
                                        </span>
                                    </label>
                                </div>
                            </ul>
                        </details>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';

export default {
    inject: ['mapId'],
    props: {
        mySmartMap: {
            type: Object,
            required: false
        },
        trans: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            filterRoutes: [],
            stops: [],
            shapes: [],
            gtfs: [],
            currentGtfs: null,
            currentRoute: null
        }
    },
    methods: {
        loadStops(stops) {
            for (const i in stops) {
                let stop = stops[i];
                const stopsGeojson = {
                    "type": "FeatureCollection",
                    "features": stop.map(({ stop_lon, stop_lat, stop_name, stop_code, stop_desc, route_id, route_color }) => ({
                        "type": 'Feature',
                        "geometry": {
                            "type": 'Point',
                            "coordinates": [stop_lon, stop_lat]
                        },
                        "properties": {
                            "stop_name": stop_name,
                            "stop_code": stop_code,
                            "stop_desc": stop_desc,
                            "route_id": route_id,
                            "route_color": route_color,
                        }
                    }))
                };

                const sourceId = 'stops' + i;
                const layerId = 'stops' + i;

                if (this.mySmartMap.getSource(sourceId)) {
                    this.mySmartMap.getSource(sourceId).setData(stopsGeojson);
                } else {
                    this.mySmartMap.addSource(sourceId, {
                        'type': 'geojson',
                        'data': stopsGeojson
                    });
                    this.mySmartMap.addLayer({
                        'id': layerId,
                        'type': 'circle',
                        'source': sourceId,
                        'paint': {
                            'circle-radius': [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 0,
                                10, 0.1,
                                15, 6,
                                18, 10
                            ],
                            'circle-color': '#ffffff',
                            'circle-stroke-width': [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 0.2,
                                10, 1,
                                15, 1.2,
                                18, 1.5
                            ],
                            'circle-stroke-color': '#3c3c3c'
                        }
                    });
                }

                this.mySmartMap.on('mouseenter', layerId, () => {
                    this.mySmartMap.getCanvas().style.cursor = 'pointer';
                });

                this.mySmartMap.on('mouseleave', layerId, () => {
                    this.mySmartMap.getCanvas().style.cursor = '';
                });

                // Ensure the circle layer is on top
                this.mySmartMap.moveLayer(layerId);
            }
        },
        loadShapes(shapes) {
            for (const i in shapes) {
                const shape = shapes[i];
                const shapesGeojson = {
                    "type": "FeatureCollection",
                    "features": [
                        {
                            "type": "Feature",
                            "geometry": {
                                "type": "LineString",
                                "coordinates": shape.map(({ shape_pt_lon, shape_pt_lat }) => [shape_pt_lon, shape_pt_lat])
                            },
                            "properties": {
                                "name": "Shapes",
                                "line_id": `line_${i}`,
                                "route_color": shape[0].route_color,
                                "route_id": shape[0].route_id
                            }
                        }
                    ]
                };

                const sourceId = 'shapes' + i;
                const layerId = 'shapes' + i;

                if (this.mySmartMap.getSource(sourceId) && this.mySmartMap.getLayer(layerId)) {
                    this.mySmartMap.moveLayer(layerId);
                } else {
                    this.mySmartMap.addSource(sourceId, {
                        'type': 'geojson',
                        'data': shapesGeojson
                    });

                    this.mySmartMap.addLayer({
                        'id': layerId,
                        'type': 'line',
                        'source': sourceId,
                        'layout': {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        'paint': {
                            'line-color': shape[0]?.route_color ? '#' + shape[0].route_color : shape[0].line_color,
                            'line-width': [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 0.2,
                                10, 1,
                                15, 2,
                                20, 4
                            ]
                        }
                    });

                    this.mySmartMap.on('mouseenter', layerId, () => {
                        this.mySmartMap.getCanvas().style.cursor = 'pointer';
                    });

                    this.mySmartMap.on('mouseleave', layerId, () => {
                        this.mySmartMap.getCanvas().style.cursor = '';
                    });
                }
            }
        },
        getGtfs() {
            axios
                .get(`/api/maps/${this.mapId}/gtfs`)
                .then((response) => {
                    this.gtfs = response.data.map(item => {
                        if (item.show_gtfs) {
                            this.filterRoutes.push(...item.routes.map(route => route.route_id));
                        }
                        return item;
                    });
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        handleFilter() {
            axios
                .post(`/api/maps/${this.mapId}/gtfs/filter`, {
                    'route_ids': this.filterRoutes
                })
                .then((response) => {
                    const { stops, shapes } = response.data;
                    this.shapes = Object.assign(shapes);
                    this.stops = Object.assign(stops);
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        chooseRoute(gtfs, route) {
            this.currentGtfs = gtfs;
            this.currentRoute = route;
        },
        clearFilter() {
            this.removeGtfsLayer();
            this.filterRoutes = [];
        },
        removeGtfsLayer() {
            for (const i in this.shapes) {
                if (this.mySmartMap.getSource('shapes' + i) && this.mySmartMap.getLayer('shapes' + i)) {
                    this.mySmartMap.removeLayer('shapes' + i);
                    this.mySmartMap.removeSource('shapes' + i);
                }
            }

            for (const i in this.stops) {
                if (this.mySmartMap.getSource('stops' + i) && this.mySmartMap.getLayer('stops' + i)) {
                    this.mySmartMap.removeLayer('stops' + i);
                    this.mySmartMap.removeSource('stops' + i);
                }
            }
        }
    },
    created() {
        this.getGtfs();
    },
    mounted() {
        setTimeout(() => {
            this.mySmartMap.on('click', (e) => {
                const features = this.mySmartMap.queryRenderedFeatures(e.point);
                if (!features.length) return;

                const layer = features[0].layer;
                const { route_id } = features[0].properties;

                if (features[0].geometry.type === 'Point' && layer.id.startsWith('stops')) {
                    for (const i in this.shapes) {
                        const shapeLayerId = 'shapes' + i;
                        const shape = this.shapes[i];
                        // Reset the line width of other shapes
                        this.mySmartMap.setPaintProperty(shapeLayerId, 'line-width', [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            5, 0.2,
                            10, 1,
                            15, 2,
                            20, 4
                        ]);

                        if (shape[0].route_id === route_id) {
                            this.mySmartMap.setPaintProperty(shapeLayerId, 'line-width', [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 0.6,
                                10, 2,
                                15, 4,
                                20, 6
                            ]);
                            this.mySmartMap.moveLayer(shapeLayerId);
                            // Move circle layer to the topmost position
                            this.mySmartMap.moveLayer(layer.id);
                        }
                    }

                    new mapboxgl.Popup()
                        .setLngLat(features[0].geometry.coordinates)
                        .setHTML(`
                        <div class="">
                            <div class="bg-base-100 rounded shadow-xl">
                                <div class="gtfs-popup">
                                    <div class="text-base font-bold mb-2">${features[0].properties.stop_name}</div>
                                    <div class="text-sm">
                                        <div class="flex">
                                            <span class="mr-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#${features[0].properties.route_color}">
                                                    <path d="M480-280q-73 0-127.5-45.5T284-440H80v-80h204q14-69 68.5-114.5T480-680q73 0 127.5 45.5T676-520h204v80H676q-14 69-68.5 114.5T480-280Zm0-80q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Z"></path>
                                                </svg>
                                            </span>
                                            ${features[0].properties.route_id}
                                        </div>
                                        <p class="gtfs-popup-route">${features[0].properties.stop_desc}</p>
                                    </div>
                                </div>
                            </div>
                        `)
                        .addTo(this.mySmartMap);

                    this.mySmartMap.moveLayer(layer.id);
                }

                if (features[0].geometry.type === 'LineString' && layer.id.startsWith('shapes')) {
                    this.mySmartMap.moveLayer(layer.id);
                    this.mySmartMap.moveLayer('stops' + features[0].properties.route_id);

                    this.mySmartMap.setPaintProperty(layer.id, 'line-width', [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        5, 0.6,
                        10, 2,
                        15, 4,
                        20, 6
                    ]);
                }
            });
        }, 1000);
    },
    watch: {
        filterRoutes() {
            this.handleFilter();
        },
        shapes(newVal, oldVal) {
            for (const oldKey in oldVal) {
                if (!Object.keys(newVal).includes(oldKey)) {
                    if (this.mySmartMap.getSource('shapes' + oldKey) && this.mySmartMap.getLayer('shapes' + oldKey)) {
                        this.mySmartMap.removeLayer('shapes' + oldKey);
                        this.mySmartMap.removeSource('shapes' + oldKey);
                    }
                }
            }
            this.loadShapes(newVal);
        },
        stops(newVal, oldVal) {
            for (const oldKey in oldVal) {
                if (!Object.keys(newVal).includes(oldKey)) {
                    if (this.mySmartMap.getSource('stops' + oldKey) && this.mySmartMap.getLayer('stops' + oldKey)) {
                        this.mySmartMap.removeLayer('stops' + oldKey);
                        this.mySmartMap.removeSource('stops' + oldKey);
                    }
                }
            }
            this.loadStops(newVal);
        }
    }
};
</script>

<style>
.wrap-text {
    white-space: normal;
    word-wrap: break-word;
}

.gtfs-popup {
    padding: 1rem;
    min-width: 30px;
}

.gtfs-popup-route {
    font-size: 0.9rem;
    margin-bottom: 7px;
}

.mapboxgl-popup-content {
    padding: 0rem !important;
    max-width: 20rem !important;
    min-width: 15rem !important;
    border-radius: 0.75rem !important;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

.mapboxgl-popup-close-button {
    width: 36px !important;
    height: 36px !important;
    font-size: 24px !important;
    line-height: 36px !important;
    border-radius: 50% !important;
}

.mapboxgl-popup-close-button svg {
    width: 24px !important;
    height: 24px !important;
}
</style>